import { logError, logSystemEvent } from './logs';

const API_ERROR_TYPES = {
	NETWORK_ERROR: 'NETWORK_ERROR',
	TIMEOUT_ERROR: 'TIMEOUT_ERROR',
	AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
	AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
	VALIDATION_ERROR: 'VALIDATION_ERROR',
	SERVER_ERROR: 'SERVER_ERROR',
	CLIENT_ERROR: 'CLIENT_ERROR',
	UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

const safeLogError = (...args) => {
	if (typeof logError === 'function') {
		logError(...args);
	}
};

const safeLogSystemEvent = (...args) => {
	if (typeof logSystemEvent === 'function') {
		logSystemEvent(...args);
	}
};

let ApiError;
try {
	const apiModule = require('./api');
	ApiError = apiModule?.ApiError;
} catch (e) {
	ApiError = null;
}

const FallbackApiError = class extends Error {
	constructor(message, type = API_ERROR_TYPES.UNKNOWN_ERROR) {
		super(message);
		this.name = 'ApiError';
		this.type = type;
	}
};

const getApiErrorClass = () => (typeof ApiError === 'function' ? ApiError : FallbackApiError);

const isApiError = error => {
	const ApiErrorClass = getApiErrorClass();
	return error instanceof ApiErrorClass || error?.name === 'ApiError';
};

export class ErrorReporter {
	static report(error, context = {}) {
		if (!error) {
			safeLogError('Error reported with null/undefined error', null, {
				context,
				userAgent: navigator.userAgent,
				url: window.location.href,
				timestamp: new Date().toISOString(),
			});
			return;
		}

		safeLogError('Error reported', error, {
			context,
			userAgent: navigator.userAgent,
			url: window.location.href,
			timestamp: new Date().toISOString(),
			...this.getErrorDetails(error),
		});

		if (this.isCriticalError(error)) {
			safeLogSystemEvent('Critical error occurred', 'error', {
				error: error.message,
				context,
				url: window.location.href,
			});
		}

		if (process.env.NODE_ENV === 'development') {
			safeLogSystemEvent('Development error details', 'debug', {
				error: error.message,
				context,
				stack: error.stack,
				additionalInfo: this.getAdditionalInfo(),
			});
		}
	}

	static getErrorDetails(error) {
		if (!error) {
			return {
				name: 'UnknownError',
				message: 'An unknown error occurred',
				stack: null,
			};
		}

		if (isApiError(error)) {
			return {
				type: error.type,
				status: error.status,
				details: error.details,
			};
		}

		return {
			name: error.name || 'UnknownError',
			message: error.message || 'An unknown error occurred',
			stack: error.stack,
		};
	}

	static isCriticalError(error) {
		if (!error) {
			return false;
		}

		if (isApiError(error)) {
			return [
				API_ERROR_TYPES.SERVER_ERROR,
				API_ERROR_TYPES.NETWORK_ERROR,
				API_ERROR_TYPES.UNKNOWN_ERROR,
			].includes(error.type);
		}

		return ['TypeError', 'ReferenceError', 'RangeError', 'URIError'].includes(error.name);
	}

	static getAdditionalInfo() {
		return {
			viewport: {
				width: window.innerWidth,
				height: window.innerHeight,
			},
			online: navigator.onLine,
			cookiesEnabled: navigator.cookieEnabled,
			language: navigator.language,
			platform: navigator.platform,
			userAgent: navigator.userAgent,
			timestamp: new Date().toISOString(),
		};
	}
}

export const ERROR_MESSAGES = {
	[API_ERROR_TYPES.NETWORK_ERROR]:
		'Network connection error. Please check your internet connection and try again.',
	[API_ERROR_TYPES.TIMEOUT_ERROR]: 'Request timed out. Please try again.',
	[API_ERROR_TYPES.AUTHENTICATION_ERROR]: 'Your session has expired. Please log in again.',
	[API_ERROR_TYPES.AUTHORIZATION_ERROR]: 'You do not have permission to perform this action.',
	[API_ERROR_TYPES.VALIDATION_ERROR]: 'Please check your input and try again.',
	[API_ERROR_TYPES.SERVER_ERROR]: 'Server error occurred. Please try again later.',
	[API_ERROR_TYPES.CLIENT_ERROR]: 'Request error. Please try again.',
	[API_ERROR_TYPES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
	DEFAULT: 'Something went wrong. Please try again.',
};

export function getUserFriendlyErrorMessage(error) {
	if (isApiError(error)) {
		return ERROR_MESSAGES[error.type] || ERROR_MESSAGES.DEFAULT;
	}

	if (error.name === 'NetworkError' || error.message.includes('fetch')) {
		return ERROR_MESSAGES[API_ERROR_TYPES.NETWORK_ERROR];
	}

	if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
		return ERROR_MESSAGES[API_ERROR_TYPES.TIMEOUT_ERROR];
	}

	return ERROR_MESSAGES.DEFAULT;
}

export function handleApiError(error, showToast = true) {
	ErrorReporter.report(error, { source: 'API' });

	const message = getUserFriendlyErrorMessage(error);

	if (showToast) {
		safeLogSystemEvent('API Error for user', 'warning', { message });
	}

	return {
		message,
		originalError: error,
		shouldRetry:
			isApiError(error) &&
			[
				API_ERROR_TYPES.NETWORK_ERROR,
				API_ERROR_TYPES.TIMEOUT_ERROR,
				API_ERROR_TYPES.SERVER_ERROR,
			].includes(error.type),
	};
}

export function handleComponentError(error, errorInfo, componentName = 'Unknown') {
	ErrorReporter.report(error, {
		source: 'React Component',
		component: componentName,
		errorInfo: {
			componentStack: errorInfo.componentStack,
		},
	});

	return {
		message: 'Something went wrong with this component. Please refresh the page.',
		originalError: error,
		errorInfo,
	};
}

export function handlePermissionError(error, context = {}) {
	ErrorReporter.report(error, {
		source: 'Permission Check',
		...context,
	});

	const message = error?.message || 'You do not have permission to perform this action.';

	safeLogSystemEvent('Permission denied', 'warning', {
		message,
		context,
		userId: context.userId,
	});

	return {
		message,
		originalError: error,
		shouldRetry: false,
	};
}

export function handleUnknownError(error, context = {}) {
	ErrorReporter.report(error, {
		source: 'Unknown Error',
		...context,
	});

	const message = getUserFriendlyErrorMessage(error);

	safeLogSystemEvent('Unknown error occurred', 'error', {
		message,
		context,
	});

	return {
		message,
		originalError: error,
		shouldRetry: false,
	};
}

export function handleAuthError(error, context = {}) {
	ErrorReporter.report(error, {
		source: 'Authentication',
		...context,
	});

	let message = 'Authentication error occurred.';

	if (error?.code === 'auth/user-not-found') {
		message = 'User not found. Please check your credentials.';
	} else if (error?.code === 'auth/wrong-password') {
		message = 'Invalid password. Please try again.';
	} else if (error?.code === 'auth/user-disabled') {
		message = 'Your account has been disabled.';
	} else if (error?.code === 'auth/too-many-requests') {
		message = 'Too many failed attempts. Please try again later.';
	} else if (error?.message) {
		message = error.message;
	}

	safeLogSystemEvent('Authentication error', 'warning', {
		message,
		context,
		errorCode: error?.code,
	});

	return {
		message,
		originalError: error,
		shouldRetry: false,
		shouldRedirectToLogin: ['auth/user-not-found', 'auth/user-disabled'].includes(error?.code),
	};
}
